import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface DocumentModalProps {
  /** Controls whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Title of the document modal */
  title: string;
  /** Markdown or raw text content of the document */
  content: string;
}

export function DocumentModal({
  isOpen,
  onClose,
  title,
  content,
}: DocumentModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Sync isOpen prop with dialog element APIs
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      if (!dialog.open) {
        dialog.showModal();
        document.body.style.overflow = 'hidden';
      }
    } else {
      if (dialog.open) {
        dialog.close();
        document.body.style.overflow = '';
      }
    }
  }, [isOpen]);

  // Clean up overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleLightDismiss = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  const handleCancel = (e: React.SyntheticEvent<HTMLDialogElement>) => {
    e.preventDefault();
    onClose();
  };

  const renderInlineFormatting = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={index} className="font-semibold text-[#F5F5F5]">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return part;
    });
  };

  const parseMarkdown = (text: string) => {
    return text.split('\n').map((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return <div key={idx} className="h-4" />;
      }
      if (trimmed.startsWith('### ')) {
        return (
          <h4 key={idx} className="text-base font-semibold text-[#F5F5F5] mt-4 mb-2">
            {trimmed.replace('### ', '')}
          </h4>
        );
      }
      if (trimmed.startsWith('## ')) {
        return (
          <h3 key={idx} className="text-lg font-bold text-[#F5F5F5] mt-6 mb-3 border-b border-[#3D3D3D] pb-1">
            {trimmed.replace('## ', '')}
          </h3>
        );
      }
      if (trimmed.startsWith('# ')) {
        return (
          <h2 key={idx} className="text-xl font-extrabold text-[#F5F5F5] mt-2 mb-4">
            {trimmed.replace('# ', '')}
          </h2>
        );
      }
      if (trimmed === '---') {
        return <hr key={idx} className="border-[#3D3D3D] my-6" />;
      }
      if (trimmed.startsWith('- ')) {
        const listItemContent = trimmed.replace('- ', '');
        return (
          <li key={idx} className="ml-5 list-disc text-sm text-[#9B9B9B] mb-2 leading-relaxed">
            {renderInlineFormatting(listItemContent)}
          </li>
        );
      }
      if (/^\d+\./.test(trimmed)) {
        const match = trimmed.match(/^(\d+\.\s*)(.*)/);
        const listItemContent = match?.[2] ?? trimmed;
        return (
          <li key={idx} className="ml-5 list-decimal text-sm text-[#9B9B9B] mb-2 leading-relaxed">
            {renderInlineFormatting(listItemContent)}
          </li>
        );
      }
      return (
        <p key={idx} className="text-sm text-[#9B9B9B] mb-4 leading-relaxed">
          {renderInlineFormatting(trimmed)}
        </p>
      );
    });
  };

  if (!isOpen) return null;

  return createPortal(
    <dialog
      ref={dialogRef}
      onCancel={handleCancel}
      onClick={handleLightDismiss}
      className="fixed inset-0 m-auto z-50 p-0 border border-[#3D3D3D] rounded-xl bg-[#231F1F] shadow-2xl flex flex-col w-[calc(100%-2rem)] max-w-3xl max-h-[85vh] backdrop:bg-black/70 backdrop:backdrop-blur-sm outline-none"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#3D3D3D] p-5 shrink-0">
        <h2
          id="document-modal-title"
          className="text-lg font-bold text-[#F5F5F5] truncate mr-4"
        >
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#3D3D3D] text-[#9B9B9B] hover:text-[#F5F5F5] hover:bg-[#333333] transition-colors cursor-pointer"
          aria-label="Cerrar modal"
          autoFocus
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3l10 10m0-10L3 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Scrollable Content Body */}
      <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)] prose prose-invert max-w-none text-left">
        {parseMarkdown(content)}
      </div>

      {/* Footer */}
      <div className="flex justify-end border-t border-[#3D3D3D] p-4 bg-[#1B1818] shrink-0 rounded-b-xl">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center rounded-lg px-5 py-2 text-sm font-semibold bg-[#36D399] text-[#141414] hover:bg-[#2dc48a] transition-colors cursor-pointer"
        >
          Entendido
        </button>
      </div>
    </dialog>,
    document.body
  );
}
